import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { push, CallHistoryMethodAction } from 'connected-react-router';

import AppState from '@webapp/store/state';

import { SimpleRelease } from '@common/types/release/simple-release';
import { RecordLabelRelation } from '@services/record-labels/types/record-label-relation';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Avatar from '@components/basics/avatar';
import Button from '@components/actions/button';
import Checkbox from '@components/form/checkbox';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import FormContainer from '@components/layout/form-container';
import moment, { Moment } from 'moment';
import { ListSubscriptions } from '../../../store/subscription-manager/action-creators';
import { DowngradeRecordLabel } from '../../../store/record-labels/action-creators';

interface DowngradeLabelPageProps {
  className?: string;
  labelName: string;
  activePlan: string;
  planEnds?: Moment;
  labelArtists: RecordLabelRelation[];
  releases: { [artistId: string]: SimpleRelease[] };
  downgrade: (artistIds: string[]) => DowngradeRecordLabel;
  goBack: () => CallHistoryMethodAction;
  listSubscriptions: () => ListSubscriptions;
}

const mapStateToProps = (state: AppState, props: any) => {
  const { clientId, id } = props.match?.params;

  const label = state.recordLabels.list.find((r) => r.id === clientId);

  const foundSubscription = state.subscriptionManager.summary?.recordLabels
    .find((s) => s.id === clientId)
    ?.subscriptions.find((s) => s.id === id);

  return {
    labelName: label?.name || '',
    planEnds: foundSubscription
      ? moment(foundSubscription.nextBilling)
      : undefined,
    activePlan: foundSubscription?.plan || '',
    labelArtists: state.recordLabels.artists[clientId] || [],
    releases: state.release.artistMap,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: any) => {
  const { clientId, id } = props.match?.params;

  return {
    downgrade: (artistIds: string[]) =>
      dispatch(new DowngradeRecordLabel(clientId, id, artistIds)),
    goBack: () => dispatch(push(`/plans-record-label/0/${clientId}/${id}`)),
    listSubscriptions: () => dispatch(new ListSubscriptions()),
  };
};

const DowngradeLabelPage: React.FC<DowngradeLabelPageProps> = ({
  listSubscriptions,
  ...props
}) => {
  const theme = useTheme();
  const [isActive, setActive] = React.useState<string[]>([]);

  const handleDowngrade = () => {
    props.downgrade(isActive);
  };

  const getBgColor = () => {
    return theme.colors.background;
  };

  const handleToggle = (artistId: string) => {
    if (isActive.includes(artistId)) {
      setActive(isActive.filter((id) => artistId !== id));
    } else {
      if (isActive.length === 2) {
        return;
      }
      setActive(isActive.concat([artistId]));
    }
  };

  React.useEffect(() => {
    listSubscriptions();
  }, [listSubscriptions]);

  return (
    <MotionOpacity className={`${props.className || ''} mt-80`}>
      <FormContainer>
        <Typography className="mb-40" type="preview-header">
          <Translate id="ONBOARDING.DOWNGRADE.LABEL.TITLE" />
        </Typography>
        <StyledBox className="mb-40">
          <Typography className="mb-30" type="section-title" weight="400">
            <Translate id="ONBOARDING.DOWNGRADE.LABEL.TEXT" /> {props.labelName}
          </Typography>
          <CheckboxList className="mb-30">
            {props.labelArtists.map((artist) => {
              const artistReleases = props.releases[artist.id] || [];
              return (
                <StyledCheckbox
                  key={artist.id}
                  iconDecoration={
                    <Avatar size="24" src={artist.artistAvatar} />
                  }
                  title={artist.artistName}
                  text={`${artistReleases.length} release${
                    artistReleases.length !== 1 ? 's' : ''
                  }`}
                  active={isActive.includes(artist.artistId)}
                  customBackground={getBgColor()}
                  disabled={
                    !isActive.includes(artist.artistId) && isActive.length === 2
                  }
                  onClick={() => handleToggle(artist.artistId)}
                />
              );
            })}
          </CheckboxList>
          <Typography className="mb-20" opacity=".5">
            <Translate id="ONBOARDING.DOWNGRADE.LABEL.COPY" />
          </Typography>
          <Typography opacity=".5">
            {props.activePlan} <Translate id="ONBOARDING.DOWNGRADE.EXPIRE" />{' '}
            {props.planEnds ? props.planEnds.format('LL') : ''}
          </Typography>
        </StyledBox>
        <ButtonGroup>
          <Button buttonType="primary" onClick={props.goBack}>
            <Translate id="ONBOARDING.DOWNGRADE.BTN.CANCEL" />
          </Button>
          <Button buttonType="secondary" onClick={handleDowngrade}>
            <Translate id="ONBOARDING.DOWNGRADE.BTN.DOWNGRADE" />
          </Button>
        </ButtonGroup>
      </FormContainer>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(DowngradeLabelPage)``);

const StyledBox = styled(Box)`
  background-color: ${({ theme }) => rgba(theme.colors.secondaryDark, 0.04)};
`;

const CheckboxList = styled.div``;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 5px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonGroup = styled(Flex)`
  flex-direction: column;
  @media ${({ theme }) => theme.device.tablet} {
    flex-direction: row;
  }
  button {
    margin-bottom: 5px;
    &:last-child {
      margin-bottom: 0;
    }
    @media ${({ theme }) => theme.device.tablet} {
      width: 50%;
      margin-right: 5px;
      margin-bottom: 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
`;
